import { useGetConversationSuspenseQuery } from "#features/conversations/api/useGetConversationSuspenseQuery.ts";
import { useCreateConversationMessage } from "#features/conversations/hooks/useCreateConversationMessage.ts";
import { findPendingQuestion, type QuestionOutcome, settlePendingQuestion } from "#features/conversations/lib/questionRecords.ts";
import { AiQueryKeys } from "#lib/AiQueryKeys.ts";
import type { QuestionAnswer, ResumeEntry } from "#types/types.ts";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";

/** The conversation's open question, and the two ways to resume its run: answer it, or dismiss it. */
export const usePendingQuestion = (conversationId: string) => {
    const { data: conversation } = useGetConversationSuspenseQuery(conversationId);
    const { createConversationMessage } = useCreateConversationMessage();
    const queryClient = useQueryClient();
    const question = findPendingQuestion(conversation)?.question;
    const interruptId = question?.interruptId;

    const resume = useCallback((entry: ResumeEntry, outcome: QuestionOutcome) => {
        // The resume goes first: the question is still pending when the send decides what it supersedes.
        createConversationMessage(conversationId, "", {
            resume: [entry],
            // The resume may have reached core despite the error, so refetch rather than trust the card's state.
            onError: () => queryClient.invalidateQueries({ queryKey: AiQueryKeys.GetConversation(conversationId) })
        });
        settlePendingQuestion(queryClient, conversationId, entry.interruptId, outcome);
    }, [conversationId, createConversationMessage, queryClient]);

    const answer = useCallback((answers: QuestionAnswer[]) => {
        if (interruptId) {
            resume({ interruptId, status: "resolved", payload: { answers, rememberForConversation: false } }, { status: "answered", answers });
        }
    }, [interruptId, resume]);

    const dismiss = useCallback(() => {
        if (interruptId) {
            resume({ interruptId, status: "cancelled" }, { status: "dismissed" });
        }
    }, [interruptId, resume]);

    return question ? { interruptId: question.interruptId, questions: question.questions, promptFeature: question.promptFeature, answer, dismiss } : null;
};
